import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'horde-theme';

/**
 * App-wide light/dark theme. The design tokens for both modes already live in `styles.css` (`:root` = light,
 * `:root.dark` = dark); this service just owns which one is active by toggling the `dark` class on `<html>` and
 * persisting the choice.
 *
 * Initial resolution (stored preference → OS preference → dark) is mirrored by the inline bootstrap script in
 * `index.html` so there is no light-flash before Angular boots.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly document = inject(DOCUMENT);

    private readonly _theme = signal<Theme>(this.resolveInitial());

    readonly theme = this._theme.asReadonly();

    readonly isDark = computed(() => this._theme() === 'dark');

    constructor() {
        this.apply(this._theme());
    }

    toggle(): void {
        this.set(this._theme() === 'dark' ? 'light' : 'dark');
    }

    set(theme: Theme): void {
        this._theme.set(theme);
        this.apply(theme);

        try {
            this.document.defaultView?.localStorage.setItem(STORAGE_KEY, theme);
        } catch {
            // LocalStorage unavailable (private mode / SSR) - theme still applies for the session.
        }
    }

    private apply(theme: Theme): void {
        this.document.documentElement.classList.toggle('dark', theme === 'dark');
    }

    private resolveInitial(): Theme {
        const view = this.document.defaultView;

        try {
            const stored = view?.localStorage.getItem(STORAGE_KEY);

            if (stored === 'light' || stored === 'dark') {
                return stored;
            }
        } catch {
            // Ignore and fall through to OS preference
        }

        const prefersLight =
            typeof view?.matchMedia === 'function' && view.matchMedia('(prefers-color-scheme: light)').matches;

        return prefersLight ? 'light' : 'dark';
    }
}
